import { forwardRef, HTMLAttributes, SyntheticEvent, useState } from 'react';
import { CardMedia, CardMediaProps, Skeleton, SkeletonProps, styled } from '@mui/material';
import classNames from 'classnames';

import { ImageType } from '@models/Image';

import { AspectRatio } from './AspectRatio';

export type CardImageWithSkeletonProps = Omit<
  CardMediaProps<'img'>,
  'component' | 'onError' | 'onLoad'
> &
  HTMLAttributes<HTMLImageElement> & {
    alt: string;
    containImage?: boolean;
    disableSkeleton?: boolean;
    disableTitle?: boolean;
    ratio: ImageType;
    SkeletonProps?: SkeletonProps;
    src?: string;
  };

const StyledCardMedia = styled(CardMedia)`
  min-height: 100%;
  min-width: 100%;
  object-fit: cover;

  &.contain {
    max-height: 100%;
    max-width: 100%;
    object-fit: contain;
  }
` as typeof CardMedia;

export const ImageWithSkeleton = forwardRef<HTMLImageElement, CardImageWithSkeletonProps>(
  (
    {
      alt,
      containImage,
      disableSkeleton,
      disableTitle,
      onError,
      onLoad,
      ratio,
      SkeletonProps: {
        height = '100%',
        sx,
        variant = 'rectangular',
        width = '100%',
        ...skeletonProps
      } = {},
      src,
      srcSet,
      title,
      ...rest
    },
    ref
  ) => {
    const [done, setDone] = useState(false);

    return (
      <AspectRatio ratio={ratio} title={title ?? (!disableTitle ? src : undefined)}>
        {(src || srcSet) && (
          <StyledCardMedia
            alt={alt}
            className={classNames({ contain: containImage })}
            component='img'
            crossOrigin='anonymous'
            key={src}
            loading='lazy'
            onError={(e: SyntheticEvent<HTMLImageElement, Event>) => {
              setDone(true);
              onError?.(e);
            }}
            onLoad={(e: SyntheticEvent<HTMLImageElement, Event>) => {
              setDone(true);
              onLoad?.(e);
            }}
            ref={ref}
            src={src}
            srcSet={srcSet}
            {...rest}
          />
        )}

        {!done && !disableSkeleton && (
          <Skeleton
            height={height}
            sx={{ position: 'absolute', ...sx }}
            variant={variant as SkeletonProps['variant']}
            width={width}
            {...skeletonProps}
          />
        )}
      </AspectRatio>
    );
  }
);
